import React, { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { HipaaLogo } from "~/icons";
import { memberIdSchema } from "~/schemas/SignUp/MemberId";
import { useSubStepTracking } from "~/shared/hooks/useFunnelTracking";
import { InsuranceProviderFormProps } from "~/shared/utils/types";
import { useSignUpStore } from "~/stores";
import { Input } from "~/ui";
import { tw } from "~/utils";
import { ActionButtons } from "../../ActionButtons";
import { ErrorMessage } from "../../ErrorMessage";
import { FormContentContainer } from "../../FormContentContainer";

export const MemberIdForm = ({
  onPrev,
  onNext,
  stepId = "primary_insurance",
  isSecondaryProvider = false,
}: InsuranceProviderFormProps & { stepId?: string }) => {
  const { patient, setPatientInsuranceField } = useSignUpStore();
  const { primaryInsurance, secondaryInsurance } = patient;

  const defaultValue = isSecondaryProvider
    ? secondaryInsurance?.memberId
    : primaryInsurance.memberId;

  const { trackComplete, trackBack, trackErrors } = useSubStepTracking({
    parentStep: isSecondaryProvider
      ? "secondary_insurance"
      : "primary_insurance",
    subStep: "member_id", // see table below
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<z.infer<typeof memberIdSchema>>({
    mode: "onBlur",
    resolver: zodResolver(memberIdSchema),
    defaultValues: {
      memberId: defaultValue ?? "",
    },
  });

  useEffect(() => {
    setValue("memberId", defaultValue ?? "");
  }, [defaultValue, setValue]);

  const onSubmit: SubmitHandler<z.infer<typeof memberIdSchema>> = (data) => {
    setPatientInsuranceField("memberId", data.memberId, isSecondaryProvider);
    trackComplete();
    onNext();
  };

  const onError = (errors: FieldErrors) => {
    trackErrors(errors);
  };

  const handlePrev = () => {
    setPatientInsuranceField("memberId", undefined, isSecondaryProvider);
    trackBack();
    onPrev();
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="w-full max-w-[570px] rounded-lg shadow-xl"
    >
      <FormContentContainer stepId={stepId}>
        <HipaaLogo className="size-20 self-center" />
        <div>
          <h1 className="font-display text-2xl">Insurance Member ID Number</h1>
        </div>
        <div className={tw("flex w-full flex-col gap-5", !errors && "mb-8")}>
          <Input
            placeholder="Member ID"
            id="memberId"
            compact
            {...register("memberId", {
              onChange: (e) => {
                const value = e.target.value;
                setValue("memberId", value, {
                  shouldValidate: true,
                });
              },
            })}
          />
        </div>
        {errors.memberId && (
          <ErrorMessage message={errors.memberId.message ?? ""} />
        )}
      </FormContentContainer>
      <ActionButtons onPrev={handlePrev} />
    </form>
  );
};
